import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import Button from '../../components/common/Button.jsx';
import Modal from '../../components/common/Modal/Modal.jsx';
import { confirmAction } from '../../utils/toastUtils.jsx';
import styles from './Admins.module.css';

const Admins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: ''
  });
  const [editingId, setEditingId] = useState(null);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/superadmin/admins');
      setAdmins(data);
    } catch (error) {
      toast.error('Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/superadmin/admins/${editingId}`, formData);
        toast.success('Admin updated successfully');
      } else {
        await api.post('/superadmin/admins', formData);
        toast.success('Admin created successfully');
      }
      setIsModalOpen(false);
      fetchAdmins();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = async (id) => {
    confirmAction('Are you sure you want to delete this admin?', async () => {
      try {
        await api.delete(`/superadmin/admins/${id}`);
        toast.success('Admin deleted successfully');
        fetchAdmins();
      } catch (error) {
        toast.error('Failed to delete admin');
      }
    });
  };

  const openEditModal = (admin) => {
    setFormData({
      fullName: admin.fullName,
      email: admin.email,
      phone: admin.phone || '',
      password: ''
    });
    setEditingId(admin._id);
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({ fullName: '', email: '', phone: '', password: '' });
    setEditingId(null);
  };

  const filteredAdmins = admins.filter(admin => 
    admin.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
    admin.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.adminsContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerText}>
          <h2>Manage Admins</h2>
          <p>Create and manage society administrator accounts</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.searchBar}>
            <Search size={18} className={styles.searchIcon} />
            <input 
              type="text" 
              placeholder="Search admins..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <button className={styles.primaryBtn} onClick={() => { resetForm(); setIsModalOpen(true); }}>
            <Plus size={18} /> Add Admin
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading admins...</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.length > 0 ? (
                filteredAdmins.map(admin => (
                  <tr key={admin._id}>
                    <td>
                      <div className={styles.adminInfo}>
                        <div className={styles.avatar}>
                          {admin.fullName.charAt(0).toUpperCase()}
                        </div>
                        <span>{admin.fullName}</span>
                      </div>
                    </td>
                    <td>{admin.email}</td>
                    <td>{admin.phone || 'N/A'}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles.approved}`}>
                        {admin.approvalStatus || 'Approved'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button className={styles.editBtn} onClick={() => openEditModal(admin)}>
                          <Edit2 size={16} />
                        </button>
                        <button className={styles.deleteBtn} onClick={() => handleDelete(admin._id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className={styles.noData}>No admins found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingId ? "Edit Admin" : "Add New Admin"}
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Full Name</label>
            <input 
              type="text" 
              name="fullName" 
              value={formData.fullName} 
              onChange={handleInputChange} 
              required 
            />
          </div>
          <div className={styles.formGroup}>
            <label>Email Address</label>
            <input 
              type="email" 
              name="email" 
              value={formData.email} 
              onChange={handleInputChange} 
              required 
            />
          </div>
          <div className={styles.formGroup}>
            <label>Phone Number</label>
            <input 
              type="text" 
              name="phone" 
              value={formData.phone} 
              onChange={handleInputChange} 
            />
          </div>
          <div className={styles.formGroup}>
            <label>{editingId ? "New Password (Optional)" : "Password"}</label>
            <input 
              type="password" 
              name="password" 
              value={formData.password} 
              onChange={handleInputChange} 
              required={!editingId}
            />
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="submit">{editingId ? "Update Admin" : "Create Admin"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Admins;
